import { AiChatView } from '../view/AiChatView';
import { templateAiChat } from '../view/template';
import { AiChatRepository } from '../repository/AiChatRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { AiChatWebComponent } from '../index';
import { AiChatData, DracoAIErrors, AiChatEvents } from '../types';
import { FeedbackFactory } from '../../common/aichat.feedback.factory';
import { GzChatCardEvents } from '../../gzChatCard/types';
import { GzTextareaWithSuggestionsEvents } from '../../../ui/gzTextareaWithSuggestions/types';
import { AvailableItemsCardEvents } from '../../gzAvailableItemsCard/types';
import { translateFn } from '../../../utils/translator.helper';

export class AiChatController extends BaseWebComponentController {
  public component: AiChatWebComponent;
  public dataRepository: AiChatRepository;
  public view: AiChatView;
  public viewConstructor: typeof AiChatView;
  public dataRepositoryConstructor: typeof AiChatRepository;

  constructor(component: AiChatWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: AiChatView,
      viewTemplate: templateAiChat,
      dataRepositoryConstructor: AiChatRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$chatTextarea,
        event: GzTextareaWithSuggestionsEvents.SEND_SUGGESTION,
        callback: this.handleSendQuestion,
      },
      {
        element: this.view.dom.$chatTextarea,
        event: GzTextareaWithSuggestionsEvents.VIEW_ANSWER,
        callback: this.handleViewAnswer,
      },
      {
        element: this.view.dom.$chatContainer,
        event: AiChatEvents.LOAD_RESPONSE_FAILED,
        callback: this.handleLoadResponseFailed,
      },
      {
        element: this.view.dom.$chatContainer,
        event: GzChatCardEvents.FEEDBACK,
        callback: this.handleChatCardFeedback,
      },
      {
        element: this.view.dom.$chatContainer,
        event: GzChatCardEvents.REGENERATE,
        callback: this.handleChatCardRegenerate,
      },
      {
        element: this.view.dom.$chatContainer,
        event: GzChatCardEvents.REFRESH,
        callback: this.handleChatCardRefresh,
      },
      {
        element: this.view.dom.$visibleAvailableItemsCard,
        event: AvailableItemsCardEvents.OUT_OF_ITEMS,
        callback: this.handleOutOfItemsEvent,
      },
      {
        element: this.view.dom.$visibleAvailableItemsCard,
        event: AvailableItemsCardEvents.HAS_AVAILABLE_ITEMS,
        callback: this.handleHasAvailableItems,
      },
      {
        element: this.view.dom.$chatContainer,
        event: 'scroll',
        callback: this.updateUserSession,
      }
    ];
  }

  private handleOutOfItemsEvent(): void {
    this.view.handleOutOfItemsEvent();
  }

  private handleHasAvailableItems(): void {
    this.view.handleHasAvailableItems();
  }

  private handleViewAnswer(event: WebComponentEvent): void {
    this.view.scrollToAnswer(event.detail.question);
  }

  private async handleSendQuestion(event: WebComponentEvent) {
    if (this.dependencies.incident.errorCode === DracoAIErrors.NO_ERROR) {
      this.view.disableInput(true, translateFn('Processing request'));
      const messageRecord = await this.view.addQuestionToChat(event.detail.suggestion);
      await this.view.addResponseToChat(messageRecord);
    }
  }

  private handleLoadResponseFailed(): void {
    this.view.disableInput(true, translateFn('Failed to load response'));
  }

  private handleChatCardFeedback(event: CustomEvent): void {
    const chatCardId = event.detail.id;
    FeedbackFactory.displayFeedBackDialog(chatCardId, this.confirmFeedback.bind(this), this.updateUserSession.bind(this));
  }

  private async confirmFeedback(event: CustomEvent, chatCardId) {
    await this.dependencies.api.clients.saveFeedback(event.detail, chatCardId);
    this.view.disableFeedbackButton(chatCardId);
  }

  private async updateUserSession() {
    return await this.dependencies.api.clients.updateCurrentUserSession();
  }

  private async handleChatCardRegenerate(event: CustomEvent) {
    const chatCardId = event.detail.id;
    await this.view.regenerateResponse(chatCardId);
  }

  private async handleChatCardRefresh(event: CustomEvent) {
    const chatCardId = event.detail.id;
    await this.view.regenerateResponse(chatCardId);
  }

  async getDataForView(): Promise<AiChatData> {
    return this.dataRepository.getDataForView();
  }
}
